import React from 'react';
import { Mic, MessageCircleQuestion, Phone } from 'lucide-react';

import CallingVoiceSettings from './CallingVoiceSettings';
import CallingSpeechToTextSettings from './CallingSpeechToTextSettings';
import CallingBehaviorSettings from './CallingBehaviorSettings';

// 2026-09-22 (Docs/CALLING_ELEVENLABS_STT_OPTION_PLAN.md, user feedback:
// "does not look user friendly" on the previous flat stack of cards):
// replaces SettingsPage.tsx's old `<CallingVoiceSettings /><CallingBehaviorSettings />`
// stack with three named sections (Voice / Speech-to-Text / Call Behavior)
// under one sticky side-nav that jump-scrolls between them -- everything
// stays on one page (no tabs, no hidden content), per the approved mockup
// (Docs/mockups/ai-calling-settings-full.html). Speech-to-Text was split
// out of CallingVoiceSettings.tsx into its own component
// (CallingSpeechToTextSettings.tsx) specifically so it could get its own
// section here.
const SECTIONS = [
  { id: 'voice', label: 'Voice', icon: Mic },
  { id: 'stt', label: 'Speech-to-Text', icon: Phone },
  { id: 'behavior', label: 'Call Behavior', icon: MessageCircleQuestion },
];

export default function AICallingSettingsLayout() {
  return (
    <div className="flex items-start gap-6">
      <nav className="sticky top-0 hidden w-44 shrink-0 flex-col gap-0.5 self-start py-1 lg:flex">
        {SECTIONS.map(({ id, label, icon: Icon }) => (
          <a
            key={id}
            href={`#ai-calling-${id}`}
            className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium text-muted-foreground no-underline hover:bg-accent hover:text-foreground"
          >
            <Icon className="h-3.5 w-3.5 shrink-0" />
            {label}
          </a>
        ))}
        <p className="mt-3 px-2.5 text-xs leading-relaxed text-muted-foreground">
          Each section saves independently — jump to any part, nothing is hidden.
        </p>
      </nav>

      <div className="min-w-0 flex-1 space-y-10">
        <section id="ai-calling-voice" className="scroll-mt-4">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent">
              <Mic className="h-4 w-4" />
            </div>
            <h2 className="text-lg font-bold">Voice</h2>
          </div>
          <p className="mb-4 ml-10 max-w-xl text-sm text-muted-foreground">
            Which text-to-speech engine the AI uses on live calls. Falls back automatically if the
            chosen provider fails.
          </p>
          <CallingVoiceSettings />
        </section>

        <section id="ai-calling-stt" className="scroll-mt-4 border-t border-border/60 pt-10">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent">
              <Phone className="h-4 w-4" />
            </div>
            <h2 className="text-lg font-bold">Speech-to-Text</h2>
          </div>
          <p className="mb-4 ml-10 max-w-xl text-sm text-muted-foreground">
            Which engine transcribes the caller's voice, and its fallback behavior.
          </p>
          <CallingSpeechToTextSettings />
        </section>

        <section id="ai-calling-behavior" className="scroll-mt-4 border-t border-border/60 pt-10">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent">
              <MessageCircleQuestion className="h-4 w-4" />
            </div>
            <h2 className="text-lg font-bold">Call Behavior</h2>
          </div>
          <p className="mb-4 ml-10 max-w-xl text-sm text-muted-foreground">
            The opening line, personality, and configurable questions the AI asks callers.
          </p>
          <CallingBehaviorSettings />
        </section>
      </div>
    </div>
  );
}
